import React, { useState, useEffect } from 'react'
import useTitle from "../../../hooks/useTitle"
import {
    PlusIcon,
    MinusIcon,
    PencilSquareIcon,
  } from "@heroicons/react/24/outline"
import PulseLoader from "react-spinners/PulseLoader"
import EtContentEditorLight2 from "../../../utils/EtContentEditorLight2"
import EtBtnFormBasic from "../../../components/common/buttons/EtBtnFormBasic"
import { useNavigate } from "react-router"
import { useCreateNewUserImprintCapabilitiesIndustryMutation, useGetUserImprintCapabilitiesIndustrysQuery } from './userImprintCapabilitiesIndustrysApiSlice'
import { useGetCapabilitiesIndustryTypesQuery } from '../../capabilitiesIndustryTypes/capabilitiesIndustryTypesApiSlice'
import { produce } from "immer";

const EtUpdateUserImprintCapabilitiesIndustryComp = ({
  userId, 
  userOrgId, 
  triggeringUrl, 
  onClose
}) => {
  // First Created - 20 Nov 2024
  // Author - Atul Pandey
  // Inherited from the EtCreateUserImprintCapabilitiesIndustry component
  // Updated - 21 Nov 2024 - added certificationOrg

  // 0 - Set Page title & variables
  useTitle('User Imprint Cpabilities')


  // 1a - set page navigations and seed variables (these will be props)
  const navigate = useNavigate()
  
  // 1b - Initialize state variables for the editor key and value
  const [editorKey, setEditorKey] = useState("capabilitiesIndustryDesc-" + Date.now())
  const [editorValue, setEditorValue] = useState("")
  const [showCreateUserImprintCapabilitiesIndustry, setShowCreateUserImprintCapabilitiesIndustry] = useState(false)
  const [selectedIndustry, setSelectedIndustry] = useState(null); // 20 Nov 2024

  // 2a - set the mutation to create the new user imprint capabilitiesIndustry data
  const [createNewUserImprintCapabilitiesIndustry, {
    isLoading,
    isSuccess,
    isError,
    error,
  }] = useCreateNewUserImprintCapabilitiesIndustryMutation()

  // 2b - Get capability industry types
  const { data: industryTypesData} = useGetCapabilitiesIndustryTypesQuery()
  const industryTypes = industryTypesData?.ids?.map(id => industryTypesData?.entities[id])
  console.log('Industry Types', industryTypes)

  // 2c - Get existing capabilities industry Data from the backend
  const { data: userImprintCapabilitiesIndustryData} = useGetUserImprintCapabilitiesIndustrysQuery()
  const userImprintCapabilitiesIndustrys = userImprintCapabilitiesIndustryData?.ids
  ?.map(id => userImprintCapabilitiesIndustryData?.entities[id])
  ?.filter(item=> item.userId._id === userId && item.userOrgId._id === userOrgId) || []
  console.log('userImprintCapabilties Industry', userImprintCapabilitiesIndustrys)

  // 2d - Update the selectedIndustry state value based on the incoming data
  useEffect(() => {
    if (userImprintCapabilitiesIndustrys?.length > 0) {
      setSelectedIndustry(userImprintCapabilitiesIndustrys[0]);
    }
  }, [userImprintCapabilitiesIndustrys]);
  
  // 3a - Initialize state variables for the form data
  const [formData, setFormData] = useState({
    userImprintCapabilitiesIndustryName: "",
    userImprintCapabilitiesIndustryDesc: "",
    userImprintCapabilitiesIndustryStats: [
      {
        industryType:null,
        yearsOfExperience: 0,
        proficiencyLevel: "Yet to Begin",
        numberOfEngagements:0,
        certifications: [
          {
            certificationName: "",
            certificationDesc: "",
            certificationDate: "",
            validTill: "",
          }
        ],
        recognitions: [
          {
            recognitionName: "",
            recognitionDesc: "",
            recognitionOrg: "",
            recognitionDate: "",
          }
        ],
      }
    ],
    userImprintCapabilitiesIndustrySelfNotes: [],
    aboutMe: "",
    pic: "",
  })

  // 3b - Use Effect to populate the formData based on the data from the backend
  useEffect(() => {
    if (selectedIndustry) {
      setFormData(
        produce((draft) => {
          draft.userImprintCapabilitiesIndustryName = selectedIndustry.userImprintCapabilitiesIndustryName || "";
          draft.userImprintCapabilitiesIndustryDesc = selectedIndustry.userImprintCapabilitiesIndustryDesc || "";
          draft.userImprintCapabilitiesIndustryStats = selectedIndustry.userImprintCapabilitiesIndustryStats?.map((stat) => ({
            ...stat,
            industryType: stat.industryType?._id || null,
          })) || [
            {
              industryType: null,
              yearsOfExperience: 0,
              proficiencyLevel: "Yet to Begin",
              numberOfEngagements: 0,
              certifications: [],
              recognitions: [],
            },
          ];
          draft.userImprintCapabilitiesIndustrySelfNotes = selectedIndustry.userImprintCapabilitiesIndustrySelfNotes || [];
          draft.aboutMe = selectedIndustry.aboutMe || "";
          draft.pic = "";
        })
      );
      setEditorValue(selectedIndustry.userImprintCapabilitiesIndustryDesc || "");
    }
  }, [selectedIndustry]);
 
  // 5 - Handle form field changes
  const handleChange = (e) => {
    const { name, value, files } = e.target;
  setFormData(
    produce((draft) => {
      if (name === "pic") {
        draft.pic = files[0];
      } else {
        draft[name] = value;
      }
    })
  );
  }

  // 5a - Handle editor value change
  const handleEditorChange = (content) => {
    setEditorValue(content)
    setFormData(prevState => ({ ...prevState, userImprintCapabilitiesIndustryDesc: content }))
  }

  // 5b - Generic Handle Add Field Function
  const handleAdd = (fieldName, newItem) => {
    setFormData(prevState => ({
      ...prevState,
      [fieldName]: [...prevState[fieldName], newItem]
    }))
  }

  // 5c - Generic Handle Remove Field Function
  const handleRemove = (fieldName, index) => {
    setFormData(prevState => ({
      ...prevState,
      [fieldName]: prevState[fieldName].filter((_, i) => i !== index)
    }))
  }

  // 5d - Function to convert pic to Base64
  const convertPicToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    }); 
 

  // 6 - Form submit logic
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Step 1: Handle the pic conversion separately
    const picBase64 = formData.pic ? await convertPicToBase64(formData.pic) : undefined;
  
    // Step 2: Use Immer to produce an immutable copy
    const payload = produce(formData, (draft) => {
      draft.userId = userId;
      draft.userOrgId = userOrgId;
      // Trim and clean up values within the draft object
      draft.userImprintCapabilitiesIndustryName =
        draft.userImprintCapabilitiesIndustryName?.trim() || undefined;
      draft.userImprintCapabilitiesIndustryDesc =
        draft.userImprintCapabilitiesIndustryDesc?.trim() || undefined;
  
      draft.userImprintCapabilitiesIndustryStats.forEach((stat) => {
        stat.certifications = stat.certifications.filter((cert) =>
          Object.values(cert).some((value) => value !== "")
        );
        stat.recognitions = stat.recognitions.filter((rec) =>
          Object.values(rec).some((value) => value !== "")
        );
      });
  
      draft.userImprintCapabilitiesIndustrySelfNotes =
        draft.userImprintCapabilitiesIndustrySelfNotes.filter(
          (note) => note.userImprintCapabilitiesIndustrySelfNoteText.trim() !== ""
        );
      draft.aboutMe = draft.aboutMe?.trim() || undefined;
      // Add the Base64 pic separately
      draft.pic = picBase64;
      
    });
  
    try {
      // Call the mutation
      await createNewUserImprintCapabilitiesIndustry(payload).unwrap();
  
      // Reset form and navigate on success
      setFormData({
        userImprintCapabilitiesIndustryName: "",
        userImprintCapabilitiesIndustryDesc: "",
        userImprintCapabilitiesIndustryStats: [
          {
            industryType: null,
            yearsOfExperience: 0,
            proficiencyLevel: "Yet to Begin",
            numberOfEngagements: 0,
            certifications: [],
            recognitions: [],
          },
        ],
        userImprintCapabilitiesIndustrySelfNotes: [],
        aboutMe: "",
        pic: "",
      });
      if (onClose) {
        onClose(); // 21 Nov 2024
      } else{
        navigate(triggeringUrl || '/userMagicHome');
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };
  
  // 7 - Reset form upon successful submission
  useEffect(() => {
    if(isSuccess) {
      setFormData({
        userImprintCapabilitiesIndustryName: "",
      userImprintCapabilitiesIndustryDesc: "",
      userImprintCapabilitiesIndustryStats: [
        {
          industryType:null,
          yearsOfExperience: 0,
          proficiencyLevel: "Yet to Begin",
          numberOfEngagements:0,
          certifications: [
            {
              certificationName: "",
              certificationDesc: "",
              certificationOrg:"",
              certificationDate: "",
              validTill: "",
            }
          ],
          recognitions: [
            {
              recognitionName: "",
              recognitionDesc: "",
              recognitionOrg: "",
              recognitionDate: "",
            }
          ],
        }
      ],
      userImprintCapabilitiesIndustrySelfNotes: [],
      aboutMe: "",
      pic: "",
      })
      // setShowCreateUserImprintCapabilitiesIndustry(false)
      if (onClose) {
        onClose(); // 21 Nov 2024
      } else{
        navigate(triggeringUrl || '/userMagicHome');
      }
    }
  
    },[isSuccess,navigate])

  // 8 - Handle Cancel
  const handleCancel = () => {
    // setShowCreateUserImprintCapabilitiesIndustry(false)
    if (onClose) {
      onClose(); // 21 Nov 2024
    } else{
      navigate(triggeringUrl || '/userMagicHome');
    }
  }

  // 9 - Handle Clear
  const handleClear = () => {
    setFormData(
      produce((draft) => {
        draft.userImprintCapabilitiesIndustryName = "";
        draft.userImprintCapabilitiesIndustryDesc = "";
        draft.userImprintCapabilitiesIndustryStats = [
          {
            industryType: null,
            yearsOfExperience: 0,
            proficiencyLevel: "Yet to Begin",
            numberOfEngagements: 0,
            certifications: [],
            recognitions: [],
          },
        ];
        draft.userImprintCapabilitiesIndustrySelfNotes = [];
        draft.aboutMe = "";
        draft.pic = "";
      })
    );
  };
 
  return (
    <div className="mx-auto m-1 p-1 bg-white rounded shadow-md shadow-black">
      {isLoading && <PulseLoader color={"#ea580c"} />}
      {isError && (
        <p className="text-orange-700 font-semi-bold py-2">
          {error?.data?.message}
        </p>
      )}

      <p className="p-1 m-1 font-bold font-poppins text-xl">Create User Imprint Capabilities - Industry</p>

      
      <form onSubmit={handleSubmit} className="m-1 p-1 text-xs font-poppins">
      {/* 1. userImprintCapabilitiesIndustryName */}
      <div id="userImprintCapabilitiesIndustryName" className="bg-gray-100 rounded p-1 m-1">
        <label htmlFor="userImprintCapabilitiesIndustryName" className="block text-gray-600 text-sm font-bold mb-1">
          Capability Name (Optional)
        </label>
        <input
          type="text"
          name="userImprintCapabilitiesIndustryName"
          placeholder="Enter a headline or title"
          value={formData.userImprintCapabilitiesIndustryName}
          onChange={handleChange}
          className="w-full rounded p-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500"
        />
      </div>
      
      {/* 2. userImprintCapabilitiesIndustryDesc */}
      <div id="userImprintCapabilitiesIndustryDesc" className="bg-gray-100 rounded p-1 m-1">
        <label htmlFor="userImprintCapabilitiesIndustryDesc" className="block text-gray-600 text-sm font-bold mb-1">
          Capability Description
        </label>
        <EtContentEditorLight2
          value={editorValue}
          handleChange={handleEditorChange}
          placeholderText="Write your capability description here!"
          editorKey={editorKey}
        />
      </div>

      {/* Industry Stats Section */}

      <div className="flex justify-between items-center m-1 p-1">
        <p className="font-bold text-sm mb-2">Industry Stats</p>
        <div className='flex justify-start items-center'>
          <p className='text-xs font-bold mx-1 px-1'>Add</p>
          <button
            type="button"
            className="bg-lime-600 hover:bg-lime-700 text-white text-xs font-bold m-1 p-1 rounded"
            onClick={() => {
              setFormData(
                produce((draft) => {
                  draft.userImprintCapabilitiesIndustryStats.push({
                    industryType: null,
                    yearsOfExperience: 0,
                    proficiencyLevel: "Yet to Begin",
                    numberOfEngagements: 0,
                    certifications: [],
                    recognitions: [],
                  });
                })
              );
            }}
          >
            <PlusIcon className="h-4 w-4 inline" /> 
          </button>
        </div>
        
      </div>

      {formData.userImprintCapabilitiesIndustryStats.map((stat, statIndex) => (
          <div key={statIndex} className="border rounded p-2 m-2 bg-gray-50">
            <div className="flex justify-between items-center mb-2">
                <p className="font-bold text-sm mb-2">Industry Stat {statIndex + 1}</p>
                <div className='flex justify-start items-center'>
                  <p className='text-xs font-bold mx-1 px-1'>Remove</p>
                  <button
                    type="button"
                    className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold m-1 p-1 rounded"
                    onClick={() => {
                      setFormData(
                        produce((draft) => {
                          draft.userImprintCapabilitiesIndustryStats.splice(statIndex, 1);
                        })
                      );
                    }}
                  >
                    <MinusIcon className="h-4 w-4 inline" /> 
                  </button>
                </div>
                
            </div>

            {/* Industry Type Dropdown */}
            <div id={`industryType-${statIndex}`} className="flex justify-between items-center bg-gray-100 rounded p-1 m-1">
              <label htmlFor={`industryType-${statIndex}`} className="text-gray-600 text-sm font-bold w-1/3">
                Industry Type
              </label>
              <select
                id={`industryType-${statIndex}`}
                value={stat.industryType || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(
                    produce((draft) => {
                      draft.userImprintCapabilitiesIndustryStats[statIndex].industryType = value;
                    })
                  );
                }}
                className="w-2/3 rounded p-2 border border-gray-300 focus:outline-none"
              >
                <option value="" disabled>
                  Select Industry Type
                </option>
                {industryTypes?.map((type) => (
                  <option key={type._id} value={type._id}>
                    {type.capabilitiesIndustryTypeName}
                  </option>
                ))}
              </select>
            </div>

              {/* Years of Experience */}
              <div id={`yearsOfExperience-${statIndex}`} className="flex justify-between items-center bg-gray-100 rounded p-1 m-1">
                <label htmlFor={`yearsOfExperience-${statIndex}`} className="text-gray-600 text-sm font-bold w-1/3">
                  Years of Experience
                </label>
                <input
                  type="number"
                  id={`yearsOfExperience-${statIndex}`}
                  name="yearsOfExperience"
                  min="0"
                  value={stat.yearsOfExperience || 0}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10) || 0;
                    setFormData(
                      produce((draft) => {
                        draft.userImprintCapabilitiesIndustryStats[statIndex].yearsOfExperience = value;
                      })
                    );
                  }}
                  className="w-2/3 rounded p-2 border border-gray-300 focus:outline-none"
                />
              </div>

              {/* Proficiency Level */}
              <div id={`proficiencyLevel-${statIndex}`} className="flex justify-between items-center bg-gray-100 rounded p-1 m-1">
                <label htmlFor={`proficiencyLevel-${statIndex}`} className="text-gray-600 text-sm font-bold w-1/3">
                  Proficiency Level
                </label>
                <select
                  id={`proficiencyLevel-${statIndex}`}
                  name="proficiencyLevel"
                  value={stat.proficiencyLevel || "Yet to Begin"}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(
                      produce((draft) => {
                        draft.userImprintCapabilitiesIndustryStats[statIndex].proficiencyLevel = value;
                      })
                    );
                  }}
                  className="w-2/3 rounded p-2 border border-gray-300 focus:outline-none"
                >
                  <option value="Yet to Begin">Yet to Begin</option>
                  <option value="Beginner">Beginner</option>
                  <option value="Advanced">Advanced</option>
                  <option value="Pro">Pro</option>
                  <option value="Expert">Expert</option>
                  <option value="Leader">Leader</option>
                </select>
              </div>

              {/* Number of Engagements */}
              <div id={`numberOfEngagements-${statIndex}`} className="flex justify-between items-center bg-gray-100 rounded p-1 m-1">
                <label htmlFor={`numberOfEngagements-${statIndex}`} className="text-gray-600 text-sm font-bold w-1/3">
                  Number of Engagements
                </label>
                <input
                  type="number"
                  id={`numberOfEngagements-${statIndex}`}
                  name="numberOfEngagements"
                  min="0"
                  value={stat.numberOfEngagements || 0}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10) || 0;
                    setFormData(
                      produce((draft) => {
                        draft.userImprintCapabilitiesIndustryStats[statIndex].numberOfEngagements = value;
                      })
                    );
                  }}
                  className="w-2/3 rounded p-2 border border-gray-300 focus:outline-none"
                />
              </div>

              {/* Certifications Section */}
              <div id={`certifications-${statIndex}`} className="border rounded p-2 m-2 bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                    <p className="font-bold text-sm">Certifications</p>
                    <div className='flex justify-start items-center'>
                      <p className='text-xs font-bold mx-1 px-1'>Add</p>
                      <button
                          type="button"
                          className="bg-lime-600 hover:bg-lime-700 text-white text-xs font-bold m-1 p-1 rounded"
                          onClick={() => {
                            setFormData(
                              produce((draft) => {
                                draft.userImprintCapabilitiesIndustryStats[statIndex].certifications.push({
                                  certificationName: "",
                                  certificationDesc: "",
                                  certificationOrg:"",
                                  certificationDate: "",
                                  validTill: "",
                                });
                              })
                            );
                          }}
                        >
                        <PlusIcon className="h-4 w-4 inline" />
                      </button>

                    </div>
                  
                </div>

                {/* Certifications List */}
                {stat.certifications.map((cert, certIndex) => (
                  <div key={`${statIndex}-${certIndex}`} className="p-2 border rounded mb-2 bg-white">
                    <div className="flex justify-between items-center">
                        <p className="font-bold text-sm">Certification {certIndex + 1}</p>
                        <div className='flex justify-start items-center'>
                            <p className='text-xs font-bold mx-1 px-1'>Remove</p>
                            <button
                            type="button"
                            className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold m-1 p-1 rounded"
                            onClick={() => {
                              setFormData(
                                produce((draft) => {
                                  draft.userImprintCapabilitiesIndustryStats[statIndex].certifications.splice(certIndex, 1);
                                })
                              );
                            }}
                          >
                            <MinusIcon className="h-4 w-4 inline" />
                          </button>
                        </div>
                      
                    </div>

                    {/* Certification Fields */}
                    <input
                      type="text"
                      placeholder="Certification Name"
                      value={cert.certificationName}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(
                          produce((draft) => {
                            draft.userImprintCapabilitiesIndustryStats[statIndex].certifications[certIndex].certificationName = value;
                          })
                        );
                      }}
                      className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring focus:ring-orange-300 mt-2"
                    />

                    <EtContentEditorLight2
                      value={cert.certificationDesc}
                      handleChange={(content) => {
                        setFormData(
                          produce((draft) => {
                            draft.userImprintCapabilitiesIndustryStats[statIndex].certifications[certIndex].certificationDesc = content;
                          })
                        );
                      }}
                      placeholderText="Write certification details here..."
                      editorKey={`certificationDesc-${statIndex}-${certIndex}`}
                    />

                    <div className="flex justify-between items-center">
                      
                      <div className="flex flex-col m-1 p-1 w-full">
                        <label
                          htmlFor={`certificationOrg-${statIndex}-${certIndex}`}
                          className="text-gray-600 text-sm font-bold"
                        >
                          Certifying Organization:
                        </label>
                        <input
                          type="text"
                          id={`certificationOrg-${statIndex}-${certIndex}`}
                          value={cert.certificationOrg || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData(
                              produce((draft) => {
                                draft.userImprintCapabilitiesIndustryStats[statIndex].certifications[certIndex].certificationOrg = value;
                              })
                            );
                          }}
                          className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring focus:ring-orange-300"
                        />
                      </div>

                      <div className="flex flex-col m-1 p-1 w-full">
                        <label
                          htmlFor={`certificationDate-${statIndex}-${certIndex}`}
                          className="text-gray-600 text-sm font-bold"
                        >
                          Certification Date
                        </label>
                        <input
                          type="date"
                          id={`certificationDate-${statIndex}-${certIndex}`}
                          value={cert.certificationDate ? cert.certificationDate.split('T')[0] : ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData(
                              produce((draft) => {
                                draft.userImprintCapabilitiesIndustryStats[statIndex].certifications[certIndex].certificationDate = value;
                              })
                            );
                          }}
                          className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring focus:ring-orange-300"
                        />
                      </div>

                      <div className="flex flex-col m-1 p-1 w-full">
                        <label
                          htmlFor={`validTill-${statIndex}-${certIndex}`}
                          className="text-gray-600 text-sm font-bold"
                        >
                          Valid Till
                        </label>
                        <input
                          type="date"
                          id={`validTill-${statIndex}-${certIndex}`}
                          value={cert.validTill ? cert.validTill.split('T')[0] : ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData(
                              produce((draft) => {
                                const cert = draft.userImprintCapabilitiesIndustryStats[statIndex].certifications[certIndex];
                                if (new Date(value) >= new Date(cert.certificationDate)) {
                                  cert.validTill = value;
                                }
                              })
                            );
                          }}
                          className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring focus:ring-orange-300"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Recognitions Section */}
              <div id={`recognitions-${statIndex}`} className="border rounded p-2 m-2 bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                    <p className="font-bold text-sm">Recognitions</p>
                    <div className='flex justify-start items-center'>
                        <p className='text-xs font-bold mx-1 px-1'>Add</p>
                        <button
                          type="button"
                          className="bg-lime-600 hover:bg-lime-700 text-white text-xs font-bold m-1 p-1 rounded"
                          onClick={() => {
                            setFormData(
                              produce((draft) => {
                                draft.userImprintCapabilitiesIndustryStats[statIndex].recognitions.push({
                                  recognitionName: "",
                                  recognitionDesc: "",
                                  recognitionOrg: "",
                                  recognitionDate: "",
                                });
                              })
                            );
                          }}
                        >
                          <PlusIcon className="h-4 w-4 inline" />
                        </button>
                    </div>
                  
                </div>

                {/* Recognitions List */}
                {stat.recognitions.map((rec, recIndex) => (
                  <div key={`${statIndex}-${recIndex}`} className="p-2 border rounded mb-2 bg-white">
                    <div className="flex justify-between items-center">
                        <p className="font-bold text-sm">Recognition {recIndex + 1}</p>
                        <div className='flex justify-start items-center'>
                            <p className='text-xs font-bold mx-1 px-1'>Remove</p>
                            <button
                            type="button"
                            className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold m-1 p-1 rounded"
                            onClick={() => {
                              setFormData(
                                produce((draft) => {
                                  draft.userImprintCapabilitiesIndustryStats[statIndex].recognitions.splice(recIndex, 1);
                                })
                              );
                            }}
                          >
                            <MinusIcon className="h-4 w-4 inline" />
                          </button>
                        </div>

                    </div>

                    {/* Recognition Fields */}
                    <input
                      type="text"
                      placeholder="Recognition Name"
                      value={rec.recognitionName}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(
                          produce((draft) => {
                            draft.userImprintCapabilitiesIndustryStats[statIndex].recognitions[recIndex].recognitionName = value;
                          })
                        );
                      }}
                      className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring focus:ring-orange-300 mt-2"
                    />

                    <EtContentEditorLight2
                      value={rec.recognitionDesc}
                      handleChange={(content) => {
                        setFormData(
                          produce((draft) => {
                            draft.userImprintCapabilitiesIndustryStats[statIndex].recognitions[recIndex].recognitionDesc = content;
                          })
                        );
                      }}
                      placeholderText="Write recognition details here..."
                      editorKey={`recognitionDesc-${statIndex}-${recIndex}`}
                    />

                    <div className="flex justify-between items-center">
                      <div className="flex flex-col m-1 p-1 w-full">
                        <label
                          htmlFor={`recognitionOrg-${statIndex}-${recIndex}`}
                          className="text-gray-600 text-sm font-bold"
                        >
                          Recognizing Org
                        </label>
                        <input
                          type="text"
                          id={`recognitionOrg-${statIndex}-${recIndex}`}
                          placeholder="Enter recognizing organization"
                          value={rec.recognitionOrg}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData(
                              produce((draft) => {
                                draft.userImprintCapabilitiesIndustryStats[statIndex].recognitions[recIndex].recognitionOrg = value;
                              })
                            );
                          }}
                          className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring focus:ring-orange-300"
                        />
                      </div>

                      <div className="flex flex-col m-1 p-1 w-full">
                        <label
                          htmlFor={`recognitionDate-${statIndex}-${recIndex}`}
                          className="text-gray-600 text-sm font-bold"
                        >
                          Recognition Date
                        </label>
                        <input
                          type="date"
                          id={`recognitionDate-${statIndex}-${recIndex}`}
                          value={rec.recognitionDate ? rec.recognitionDate.split('T')[0] : ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData(
                              produce((draft) => {
                                draft.userImprintCapabilitiesIndustryStats[statIndex].recognitions[recIndex].recognitionDate = value;
                              })
                            );
                          }}
                          className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring focus:ring-orange-300"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
          </div>
      ))}

      {/* Notes Section */}
      <div id="selfNotes" className="border rounded p-2 m-2 bg-gray-50">
        <div className="flex justify-between items-center mb-2">
            <p className="font-bold text-sm">Notes</p>
            <div className='flex justify-start items-center'>
                <p className='text-xs font-bold mx-1 px-1'>Add</p>
                <button
                type="button"
                className="bg-lime-600 hover:bg-lime-700 text-white text-xs font-bold m-1 p-1 rounded"
                onClick={() => {
                  setFormData(
                    produce((draft) => {
                      draft.userImprintCapabilitiesIndustrySelfNotes.push({
                        userImprintCapabilitiesIndustrySelfNoteText: "",
                        userImprintCapabilitiesIndustrySelfNoteDate: new Date().toISOString(),
                        userImprintCapabilitiesIndustrySelfNoteUpdateDate: null,
                      });
                    })
                  );
                }}
              >
                <PlusIcon className="h-4 w-4 inline" />
              </button>
            </div>
          
        </div>

        {formData.userImprintCapabilitiesIndustrySelfNotes?.map((note, noteIndex) => (
          <div key={noteIndex} className="p-2 border rounded mb-2 bg-white">
            <div className="flex justify-between items-center">
                  <p className="font-bold text-sm">Self Note: {noteIndex + 1}</p>
              <div className='flex justify-start items-center'>
                  <p className='text-xs font-bold mx-1 px-1'>Remove</p>
                  <button
                    type="button"
                    className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold m-1 p-1 rounded"
                    onClick={() => {
                      setFormData(
                        produce((draft) => {
                          draft.userImprintCapabilitiesIndustrySelfNotes.splice(noteIndex, 1);
                        })
                      );
                    }}
                  >
                    <MinusIcon className="h-4 w-4 inline" />
                  </button>
              </div>
              
            </div>

            {/* Note Text */}
            <EtContentEditorLight2
              value={note.userImprintCapabilitiesIndustrySelfNoteText}
              handleChange={(content) => {
                setFormData(
                  produce((draft) => {
                    draft.userImprintCapabilitiesIndustrySelfNotes[noteIndex].userImprintCapabilitiesIndustrySelfNoteText = content;
                    draft.userImprintCapabilitiesIndustrySelfNotes[noteIndex].userImprintCapabilitiesIndustrySelfNoteUpdateDate = new Date().toISOString();
                  })
                );
              }}
              placeholderText="Write your note here..."
              editorKey={`note-${noteIndex}`}
            />

            {/* Note Date (Read-Only) */}
            <p className="text-xs text-gray-500 mt-2">
              Created on: {new Date(note.userImprintCapabilitiesIndustrySelfNoteDate).toLocaleDateString()}
              {note.userImprintCapabilitiesIndustrySelfNoteUpdateDate && (
                <span>
                  , Updated on: {new Date(note.userImprintCapabilitiesIndustrySelfNoteUpdateDate).toLocaleDateString()}
                </span>
              )}
            </p>
          </div>
        ))}
      </div>

      <div id="actions" className="bg-white m-1 px-1 py-3 rounded">
              <EtBtnFormBasic
                name={"Update"}
                type={"create"}
                handleClick={handleSubmit}
              />
              <EtBtnFormBasic
                name={"Clear"}
                type={"clear"}
                handleClick={handleClear}
              />
              <EtBtnFormBasic
                name={"Cancel"}
                type={"cancel"}
                handleClick={handleCancel}
              />
            </div>

    </form>

    </div>
  )
}

export default EtUpdateUserImprintCapabilitiesIndustryComp